'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { isOfficeAttachmentMimeType, type Attachment } from '../types/attachment';
import { getDownloadUrl } from '../lib/api/attachments';

type AttachmentPreviewContentProps = {
  token: string | null;
  attachment: Attachment | null;
  onDownloadUrlChange?: (url: string | null) => void;
};

export function AttachmentPreviewContent({ token, attachment, onDownloadUrlChange }: AttachmentPreviewContentProps) {
  const t = useTranslations('documents.editor');
  const tAttachment = useTranslations('components.attachment');
  const tCommon = useTranslations('common');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // 새 attachment로 바뀌면 이전 presigned URL을 즉시 무효화한다.
    // 그렇지 않으면 헤더 파일명과 다운로드 href가 잠시 어긋나 직전 파일이 다운로드될 수 있다.
    setDownloadUrl(null);
    setTextContent('');
    onDownloadUrlChange?.(null);

    if (!attachment || !token) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const result = await getDownloadUrl(token, attachment.id);
        if (cancelled) return;
        setDownloadUrl(result.downloadUrl);
        onDownloadUrlChange?.(result.downloadUrl);
        if (attachment.mimeType === 'text/plain' || attachment.mimeType === 'text/markdown') {
          const response = await fetch(result.downloadUrl);
          if (!cancelled) {
            setTextContent(await response.text());
          }
        } else {
          setTextContent('');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attachment, token, onDownloadUrlChange]);

  if (!attachment) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--color-fg-muted)]">
        {tAttachment('noFileSelected')}
      </div>
    );
  }

  if (isLoading || !downloadUrl) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--color-fg-muted)]">
        {tCommon('loading')}
      </div>
    );
  }

  if (attachment.mimeType.startsWith('image/')) {
    return (
      <div className="flex h-full items-center justify-center bg-base-200">
        {/* eslint-disable-next-line @next/next/no-img-element -- attachment previews use runtime download URLs. */}
        <img src={downloadUrl} alt={attachment.originalName} className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  if (attachment.mimeType === 'text/markdown') {
    return (
      <div className="h-full overflow-auto bg-base-200 p-4">
        <div className="prose max-w-none rounded-lg bg-base-100 p-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{textContent}</ReactMarkdown>
        </div>
      </div>
    );
  }

  if (attachment.mimeType === 'text/plain') {
    return (
      <div className="h-full overflow-auto bg-base-200 p-4">
        <SyntaxHighlighter language="text" customStyle={{ margin: 0, borderRadius: '0.5rem' }}>
          {textContent}
        </SyntaxHighlighter>
      </div>
    );
  }

  if (attachment.mimeType === 'application/pdf') {
    return <iframe title={attachment.originalName} src={downloadUrl} className="h-full w-full bg-white" />;
  }

  if (attachment.mimeType === 'text/html') {
    return <iframe title={attachment.originalName} src={downloadUrl} sandbox="" className="h-full w-full bg-white" />;
  }

  if (isOfficeAttachmentMimeType(attachment.mimeType)) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-[var(--color-fg-muted)]">
        {t('attachmentOfficePreviewUnavailable')}
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center text-sm text-[var(--color-fg-muted)]">
      {tAttachment('previewUnavailable')}
    </div>
  );
}
